import { todayInSaoPaulo } from '@supply/contracts'
import { Bell, CheckCheck } from 'lucide-react'
import * as React from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { listNotifications, markNotificationsRead, sweepDecisionNotifications, sweepDueNotifications, sweepMeetingNotifications } from '@/data/api'
import type { Notification } from '@/data/types'
import { invalidate, useLoad } from '@/data/use-load'
import { formatDateTime } from '@/lib/format'
import { cn } from '@/lib/utils'

const SWEEP_KEY = 'supply-sweep-day'

/**
 * Gera os avisos de prazo do dia uma vez por sessão/dia (idempotente no
 * banco: `sweep_due_notifications` não duplica). Sem pg_cron nem worker.
 */
function useDailySweep() {
  React.useEffect(() => {
    const today = todayInSaoPaulo()
    let last: string | null = null
    try {
      last = window.localStorage.getItem(SWEEP_KEY)
    } catch {
      /* storage indisponível: varre de novo, sem problema */
    }
    if (last === today) return
    void sweepDueNotifications().then((r) => {
      if (!r.ok) return
      try {
        window.localStorage.setItem(SWEEP_KEY, today)
      } catch {
        /* storage indisponível */
      }
      if (r.value > 0) invalidate()
    })
  }, [])
}

/**
 * RAS/Q3 atrasada: varre a cada abertura do app (não só uma vez por dia), para
 * a reunião que passou do dia ideal avisar no mesmo dia em que alguém mudar o
 * dia ideal. Barato e idempotente: cada reunião avisa uma vez só.
 */
function useMeetingSweep() {
  React.useEffect(() => {
    void sweepMeetingNotifications().then((r) => {
      if (r.ok && r.value > 0) invalidate()
    })
  }, [])
}

/**
 * Follow-up de decisão vencida ao superior: varre a cada abertura (o banco
 * avisa uma vez por decisão e prazo; o pg_cron, onde existe, cobre quem não
 * abriu o app).
 */
function useDecisionSweep() {
  React.useEffect(() => {
    void sweepDecisionNotifications().then((r) => {
      if (r.ok && r.value > 0) invalidate()
    })
  }, [])
}

export function NotificationBell() {
  useDailySweep()
  useMeetingSweep()
  useDecisionSweep()
  const state = useLoad(() => listNotifications(20), [], { pollMs: 60_000 })
  const [open, setOpen] = React.useState(false)
  const unread = (state.data ?? []).filter((n) => !n.read_at)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon-sm" aria-label={unread.length ? `Avisos: ${unread.length} não lidos` : 'Avisos'} className="relative">
          <Bell />
          {unread.length > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 font-mono text-[10px] tabular-nums text-destructive-foreground">
              {unread.length > 9 ? '9+' : unread.length}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[340px] p-0">
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <span className="text-sm font-medium">Avisos</span>
          {unread.length > 0 && (
            <Button variant="ghost" size="sm" onClick={() => void markNotificationsRead().then((r) => r.ok && invalidate())}>
              <CheckCheck /> Marcar todos
            </Button>
          )}
        </div>
        <NotificationList items={state.data ?? []} onNavigate={() => setOpen(false)} compact />
        <div className="border-t border-border px-3 py-2 text-right">
          <Link to="/notificacoes" className="text-xs text-primary hover:underline" onClick={() => setOpen(false)}>
            Ver todos
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export function NotificationList({ items, onNavigate, compact }: { items: Notification[]; onNavigate?: () => void; compact?: boolean }) {
  const navigate = useNavigate()
  if (!items.length) return <p className="px-3 py-6 text-center text-sm text-muted-foreground">Nenhum aviso.</p>
  return (
    <ul className={cn('divide-y divide-border', compact && 'max-h-[360px] overflow-y-auto')}>
      {items.map((n) => (
        <li key={n.id}>
          <button
            type="button"
            className={cn('flex w-full gap-2 px-3 py-2.5 text-left hover:bg-accent', !n.read_at && 'bg-primary/5')}
            onClick={() => {
              if (!n.read_at) void markNotificationsRead([n.id]).then((r) => r.ok && invalidate())
              onNavigate?.()
              if (n.link) void navigate(n.link)
            }}
          >
            <span aria-hidden className={cn('mt-1.5 h-2 w-2 shrink-0 rounded-full', n.read_at ? 'bg-transparent' : 'bg-primary')} />
            <span className="min-w-0">
              <span className="block text-sm font-medium">{n.title}</span>
              {n.body && <span className="block truncate text-xs text-muted-foreground">{n.body}</span>}
              <span className="block font-mono text-[11px] tabular-nums text-muted-foreground">{formatDateTime(n.created_at)}</span>
            </span>
          </button>
        </li>
      ))}
    </ul>
  )
}
